import { DEFAULT_COLOR_HEX } from 'companySettings/components/PartFinderAttributeValues/constants';
import { AttributeStatus, AttributeType } from 'companySettings/enums';
import {
  IPartFinderAttribute,
  IPartFinderProductType,
  IPartFinderProductTypeAttribute,
  IPartFinderProductTypeAttributePayload,
  IPartFinderProductTypePayload,
} from 'companySettings/types';

import {
  AttributeValueSource,
  IUpsertPartFinderProductTypeFormData,
  IUpsertProductTypeAttributeFormData,
} from './types';
import { copyListValues } from './utils';

// An attribute without a list sends no sourceAttributeId: the server reads a missing one as none.
// A list's version number goes only with the list it belongs to. The values go with their stored
// columns only, in their order, which is their position.
export const mapAttributePayload = (
  attribute: IPartFinderProductTypeAttribute
): IPartFinderProductTypeAttributePayload => ({
  ...(attribute.id && { id: attribute.id }),
  name: attribute.name?.trim(),
  type: attribute.type,
  ...(attribute.sourceAttributeId && { sourceAttributeId: attribute.sourceAttributeId }),
  ...(attribute.sourceAttributeId &&
    attribute.sourceVersionNumber && { sourceVersionNumber: attribute.sourceVersionNumber }),
  ...(attribute.updateList && { updateList: true }),
  values: copyListValues(attribute.values),
});

// An edit also sends the revision it was made on, so the server can refuse it when someone else
// saved the product type in the meantime.
export const mapPartFinderProductTypePayload = (
  data: IUpsertPartFinderProductTypeFormData,
  status: AttributeStatus,
  productType?: IPartFinderProductType
): IPartFinderProductTypePayload => ({
  ...(productType && { id: productType.id, revision: productType.revision }),
  name: data?.name?.trim(),
  status,
  attributes: (data?.attributes ?? []).map(mapAttributePayload),
});

// An attribute reopened before the product type is saved keeps its pending write-back to its list.
export const mapAttributeFormDefaults = (
  attribute?: IPartFinderProductTypeAttribute
): IUpsertProductTypeAttributeFormData => ({
  name: attribute?.name ?? '',
  newColorHex: DEFAULT_COLOR_HEX,
  newColorName: '',
  newRangeIncrement: '',
  newRangeMax: '',
  newRangeMin: '',
  newRangeUnit: '',
  newValue: '',
  newListName: '',
  saveAsList: false,
  source: attribute?.sourceAttributeId
    ? AttributeValueSource.VALUE_LIST
    : AttributeValueSource.TYPED,
  sourceAttributeId: attribute?.sourceAttributeId ?? null,
  sourceVersionNumber: attribute?.sourceVersionNumber ?? null,
  type: attribute?.type ?? AttributeType.VALUES,
  updateList: Boolean(attribute?.updateList),
  values: attribute?.values ?? [],
});

// The attribute the drawer hands back to the product type form. In list mode it keeps the list
// its values came from, with the list version they were copied from, and asks for the write-back
// when told to. Typed values saved as a new list are bound to that list: they are its values, so
// there is nothing to write back.
export const mapAttributeFromForm = (
  data: IUpsertProductTypeAttributeFormData,
  lists: IPartFinderAttribute[],
  attribute?: IPartFinderProductTypeAttribute,
  createdList?: IPartFinderAttribute
): IPartFinderProductTypeAttribute => {
  const isListMode = AttributeValueSource.VALUE_LIST === data.source;
  const sourceAttributeId = isListMode ? data.sourceAttributeId ?? null : createdList?.id ?? null;
  const sourceList = isListMode ? lists.find((list) => list.id === sourceAttributeId) : createdList;

  return {
    ...(attribute?.id && { id: attribute.id }),
    sourceAttributeId,
    sourceAttributeName: sourceList?.name ?? null,
    sourceVersionNumber: isListMode
      ? data.sourceVersionNumber ?? null
      : createdList?.versionNumber ?? null,
    name: data.name?.trim(),
    type: data.type,
    updateList: isListMode && Boolean(sourceAttributeId && data.updateList),
    values: data.values ?? [],
  };
};
